import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { EditFormDetails } from "../interfaces";
import { useQuery } from "react-query";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { checkErrors } from "../../services/Category";
import { currentUserCurrencyAtom } from "../../AuthProvider";
import { useRecoilValue } from "recoil";
import { useFormikContext } from "formik";

export enum EVariationType {
  PERCENTAGE = "PERCENTAGE",
  VALUE = "VALUE",
}

interface BusinessRuleActionResponseResult {
  id: string;
  label: string;
}

interface BusinessRuleActionResponse {
  count: number;
  next: any;
  previous: any;
  results: BusinessRuleActionResponseResult[];
}

interface BusinessRuleActionParameter {
  variation_type: EVariationType;
  variation_value: string;
}

interface BusinessRuleActionValue {
  type: string;
  parameters: BusinessRuleActionParameter;
}

/**
 * Format value (from props) to variation value.
 */
const formatValueToVariationValue = (
  variationType: BusinessRuleActionParameter["variation_type"],
  variationValue: BusinessRuleActionParameter["variation_value"]
): string => {
  return variationType === EVariationType.PERCENTAGE
    ? `${parseFloat(variationValue) * 100}`
    : variationValue;
};

/**
 * BusinessRuleAction form component.
 */
export const BusinessRuleAction = (params: {
  filter: EditFormDetails;
  name: string;
  value: BusinessRuleActionValue[] | null;
  helpers: any;
  classes: any;
}) => {
  const currency = useRecoilValue(currentUserCurrencyAtom);
  const { classes, filter, value, name } = params;
  const { setFieldValue } = useFormikContext();
  const [initialValue] = useState(
    value && value[0]
      ? value[0]
      : {
          type: "PRICE_VARIATION",
          parameters: {
            variation_type: EVariationType.PERCENTAGE,
            variation_value: "0",
          },
        }
  );
  const [options, setOptions] = useState<BusinessRuleActionResponseResult[]>(
    []
  );
  const [actionType, setActionType] =
    useState<BusinessRuleActionResponseResult["id"]>();
  const [variationType, setVariationType] = useState<EVariationType>(
    initialValue.parameters.variation_type
  );
  const [variationValue, setVariationValue] = useState<
    BusinessRuleActionParameter["variation_value"]
  >(
    formatValueToVariationValue(
      initialValue.parameters.variation_type,
      initialValue.parameters.variation_value
    )
  );

  /**
   * Fetch business rule actions from API endpoint.
   */
  const fetchFn = async (): Promise<BusinessRuleActionResponse> => {
    const url = new URL(`/api/business-rules/actions`, window.origin);
    const response = await fetch(url.toString());

    checkErrors(response);
    return response.json();
  };

  const query = useQuery<BusinessRuleActionResponse>(
    ["/business-rules/actions"],
    () => fetchFn(),
    {
      retry: false,
    }
  );

  /**
   * On change variation.
   * @param selectedId
   */
  const onChangeVariation = (
    selectedId: BusinessRuleActionResponseResult["id"] | null
  ) => {
    if (selectedId) {
      const optionIdFound = options.find(({ id }) => id === selectedId)?.id;

      if (optionIdFound) {
        setActionType(optionIdFound);
      }
    }
  };

  /**
   * On change variation value.
   */
  const onChangeVariationValue = (value: string): void => {
    setVariationValue(value.replace(",", "."));
  };

  /**
   * Set value to parent component.
   */
  const handleNewValue = useCallback(
    (
      variationValue: BusinessRuleActionParameter["variation_value"],
      variationType: EVariationType,
      actionType: BusinessRuleActionResponseResult["id"]
    ) => {
      let variationValueTmp = variationValue;
      if (variationType === EVariationType.PERCENTAGE) {
        variationValueTmp = variationValue
          ? `${parseFloat(variationValue) / 100}`
          : "0";
      }
      setFieldValue(name, [
        {
          // action_id: options[0]?.id, // TODO: Backend change have to be made to handle a collection of actions
          type: actionType,
          parameters: {
            variation_type: variationType,
            variation_value: variationValueTmp,
          },
        },
      ]);
    },
    [setFieldValue, name]
  );

  /**
   * On data fetch.
   */
  useEffect(() => {
    const results = query.data?.results;
    setOptions(results || []);

    if (results && results[0]) {
      setActionType(results[0].id);
    }
  }, [query.data]);

  /**
   * Propagate value to parent component.
   */
  useEffect(() => {
    if (actionType && variationType && variationValue) {
      handleNewValue(variationValue, variationType, actionType);
    }
  }, [actionType, variationType, variationValue, handleNewValue]);

  return (
    <Box>
      <InputLabel id="select-business-rule-action">
        <FormattedMessage id={filter.label} />
      </InputLabel>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid size={6}>
          <FormControl
            className={classes.select}
            disabled={filter.readonly}
            fullWidth
            variant="outlined"
          >
            <Select
              variant="outlined"
              name={"variation_id"}
              fullWidth
              value={actionType || ""}
              onChange={(event) =>
                onChangeVariation(
                  event.target.value as BusinessRuleActionResponseResult["id"]
                )
              }
            >
              {options.map(({ id, label }) => (
                <MenuItem key={id} value={id}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={3}>
          <FormControl
            className={classes.numeric}
            disabled={filter.readonly}
            fullWidth
            variant="outlined"
          >
            <TextField
              className={classes.inputText}
              sx={{ marginTop: 0 }}
              value={variationValue}
              onChange={(e) => onChangeVariationValue(e.target.value)}
              slotProps={{
                input: filter?.inputProps,
              }}
            />
          </FormControl>
        </Grid>
        <Grid size={3}>
          <FormControl
            className={classes.select}
            disabled={filter.readonly}
            fullWidth
            variant="outlined"
          >
            <Select
              variant="outlined"
              name={"variation_type"}
              value={variationType}
              fullWidth
              onChange={(event) =>
                setVariationType(event.target.value as EVariationType)
              }
            >
              <MenuItem
                key={EVariationType.PERCENTAGE}
                value={EVariationType.PERCENTAGE}
              >
                %
              </MenuItem>
              <MenuItem key={EVariationType.VALUE} value={EVariationType.VALUE}>
                {currency.symbol}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};
